/**
 * BannerAtualizacaoSW — banner discreto no canto inferior direito quando o
 * service worker detecta nova versão disponível.
 *
 * Como funciona:
 *  1. `serviceWorkerRegistration.ts` registra o SW. Quando o browser baixa
 *     uma versão nova e ela termina de instalar (já tem SW antigo controlando
 *     a página), dispara `window.dispatchEvent(new CustomEvent('sw-update-ready', { detail: { reg } }))`.
 *  2. Esse componente escuta o evento, guarda a `ServiceWorkerRegistration`
 *     e exibe o banner.
 *  3. Clique em "Atualizar" → manda `SKIP_WAITING` pro SW novo, espera o
 *     `controllerchange` e dá `window.location.reload()`.
 *  4. Clique em "Depois" → esconde o banner até a próxima vez que o evento
 *     dispare (próxima nova versão).
 */
import { useEffect, useState } from 'react';
import { Affix, Paper, Group, Button, Text, ActionIcon } from '@mantine/core';
import { IconRefresh, IconX, IconSparkles } from '@tabler/icons-react';
import { aplicarAtualizacaoSW } from '../serviceWorkerRegistration';

export default function BannerAtualizacaoSW() {
  const [reg, setReg] = useState<ServiceWorkerRegistration | null>(null);
  const [visivel, setVisivel] = useState(false);
  const [atualizando, setAtualizando] = useState(false);

  useEffect(() => {
    function handler(e: Event) {
      const detail = (e as CustomEvent).detail;
      const r = detail?.reg as ServiceWorkerRegistration | undefined;
      if (r) {
        setReg(r);
        setVisivel(true);
      }
    }
    window.addEventListener('sw-update-ready', handler);
    return () => window.removeEventListener('sw-update-ready', handler);
  }, []);

  function atualizar() {
    if (!reg) return;
    setAtualizando(true);
    // Quando o SW novo assumir o controle, recarrega a página.
    // Usamos `once: true` pra evitar disparar várias vezes.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    }, { once: true });
    aplicarAtualizacaoSW(reg);
    // Fallback: se controllerchange não disparar em 4s, força reload mesmo assim
    setTimeout(() => window.location.reload(), 4000);
  }

  function depois() {
    setVisivel(false);
  }

  if (!visivel) return null;

  return (
    <Affix position={{ bottom: 24, right: 24 }} zIndex={2000}>
      <Paper
        shadow="lg"
        radius="md"
        p="md"
        withBorder
        style={{
          maxWidth: 360,
          minWidth: 280,
          background: '#fff',
          borderColor: '#D8DEFC',
        }}
      >
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
          <Group gap={8} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <IconSparkles size={20} color="#0930D0" style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ minWidth: 0 }}>
              <Text fw={600} size="sm" c="#020D7F">
                Nova versão disponível
              </Text>
              <Text size="xs" c="dimmed" mt={2}>
                Atualize pra carregar as últimas melhorias do sistema.
              </Text>
            </div>
          </Group>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            onClick={depois}
            aria-label="Dispensar"
            disabled={atualizando}
          >
            <IconX size={14} />
          </ActionIcon>
        </Group>
        <Group justify="flex-end" gap={6} mt="sm">
          <Button
            variant="subtle"
            color="gray"
            size="xs"
            onClick={depois}
            disabled={atualizando}
          >
            Depois
          </Button>
          <Button
            color="brand"
            size="xs"
            leftSection={<IconRefresh size={14} />}
            loading={atualizando}
            onClick={atualizar}
          >
            Atualizar agora
          </Button>
        </Group>
      </Paper>
    </Affix>
  );
}
